import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, DestroyRef, effect, ElementRef, EventEmitter, inject, input, OnDestroy, OnInit, output, Output, QueryList, signal, untracked, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { saveAs } from 'file-saver';
import { ChevronLeft, ChevronRight } from 'lucide-angular';
import { SliderModule } from 'primeng/slider';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { DeleteConfirmationComponent } from 'src/app/@core/components/ui/delete-confirmation/delete-confirmation.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { IconComponent, IconSizeEnum, IconSourceEnum } from 'src/app/@core/components/ui/icon/icon.component';
import { CustomMenuItem, MenuComponent } from 'src/app/@core/components/ui/menu/menu.component';
import { ZardResizableHandleComponent } from 'src/app/@core/components/ui/resizable/resizable-handle.component';
import { ZardResizablePanelComponent } from 'src/app/@core/components/ui/resizable/resizable-panel.component';
import { ZardResizableComponent, ZardResizeEvent } from 'src/app/@core/components/ui/resizable/resizable.component';
import { MOBILE_BREAKPOINT } from 'src/app/@core/constants/breakpoints';
import { IWorkspace } from 'src/app/@core/models/model';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { BlurScreenService } from 'src/app/@core/services/blur-screen.service';
import { BrowserAgentService } from 'src/app/@core/services/browser-agent.service';
import { CLIENT_EVENTS, SocketService } from 'src/app/@core/services/socket.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { FileItem } from 'src/app/@shared/components/file-selection/file-selection.model';
import { ImagePreviewComponent } from 'src/app/@shared/components/image-preview/image-preview.component';
import { TruncatePipe } from 'src/app/@shared/pipes/truncate.pipe';
import { threadKeys } from 'src/app/angular-query/keys/thread-keys';
import { OrganizationSetupFormService } from 'src/app/contract-management/services/organization-setup-from.service';
import { UserService } from 'src/app/contract-management/services/user.service';
import { UserCreditsStateService } from 'src/app/features/credits/services/user-credits-state.service';
import { TOOLS } from 'src/app/features/general-agent/constants/tool.constants';
import { v4 as uuidv4 } from 'uuid';
import { EditThreadComponent } from '../../components/dialogs/edit-thread/edit-thread.component';
import { ShareThreadComponent } from '../../components/dialogs/share-thread/share-thread.component';
import { FilePreviewComponent } from '../../components/panels/file-preview/file-preview.component';
import { SourcesPanelComponent } from '../../components/panels/source-panel/sources-panel.component';
import { ToolPanelComponent } from '../../components/panels/tool-panel/tool-panel.component';
import { MessageListComponent } from '../../components/thread/message-list/message-list.component';
import { ThreadFilesComponent } from '../../components/thread/thread-files/thread-files.component';
import { ThreadInputComponent } from '../../components/thread/thread-input/thread-input.component';
import { CanvasComponent } from '../../components/tool-views/canvas/canvas.component';
import { ThreadSkeletonComponent } from '../../components/ui/thread-skeleton/thread-skeleton.component';
import { IThread, IThreadMetadata, LLM_PROVIDERS, MESSAGE_TYPE, MessageItem, THREAD_METADATA_STATUS, THREAD_STREAMING_STATUS } from '../../models/model';
import { AGENT_MODE, ChatAttachment, ChatMessagePayload, FILE_GROUP, MESSAGE_FEEDBACK, PLAN_FEEDBACK, RestoredInputState } from '../../models/types';
import { FollowUpsService } from '../../services/follow-ups.service';
import { ICreatePromptPayload, PromptLibraryService } from '../../services/prompt-library.service';
import { SidePanelService } from '../../services/side-panel.service';
import { SourcesPanelService } from '../../services/sources-panel.service';
import { getSubagentTaskId, isSubagentNamespace, PROMPT_ENHANCEMENT_INTERRUPT, PROMPT_REUSE_INTERRUPT, ThreadMessagesService } from '../../services/thread-messages.service';
import { ToolCallPanelService } from '../../services/tool-call-panel.service';
import { isFeatureEnabled as checkFeatureEnabled, IThreadAgentConfig, THREAD_AGENT_CONFIG, THREAD_AGENT_FEATURES } from '../../tokens/thread-agent-config.token';
import { THREAD_AGENT_SERVICE } from '../../tokens/thread-agent.token';
import { copyCanvasContent, getToolIcon, isSummarizationMessage } from '../../utils/utils';
import { PromptEditorDialogComponent } from '../prompt-library/components/prompt-editor-dialog/prompt-editor-dialog.component';


export enum CHECKPOINT_MODE {
  EDIT = 'EDIT',
  RETRY = 'RETRY',
}

/**
 * ActiveThreadComponent
 * 
 * Displays an existing conversation thread with messages and input.
 * 
 * Responsibilities:
 * - Load and display thread messages
 * - Handle message streaming
 * - Show thread header with actions
 * - Provide input for sending new messages
 * - Handle pause/resume of agent execution
 * - This component is focused ONLY on displaying and interacting with
 * - an existing thread.
 */

@Component({
  selector: 'app-active-thread',
  standalone: true,
  host: { class: 'flex flex-col flex-1 min-h-0 h-full overflow-hidden' },
  imports: [
    CommonModule,
    FormsModule,
    IconComponent,
    ThreadInputComponent,
    MessageListComponent,
    ShareThreadComponent,
    ThreadFilesComponent,
    EditThreadComponent,
    DeleteConfirmationComponent,
    MenuComponent,
    ButtonComponent,
    TooltipModule,
    ZardResizableComponent,
    ZardResizablePanelComponent,
    ZardResizableHandleComponent,
    ToolPanelComponent,
    SourcesPanelComponent,
    SliderModule,
    DialogComponent,
    TruncatePipe,
    ThreadSkeletonComponent,
    ImagePreviewComponent,
    FilePreviewComponent,
    CanvasComponent,
    PromptEditorDialogComponent,
  ],
  templateUrl: './active-thread.component.html',
  styleUrl: './active-thread.component.scss',
  animations: [
    trigger('fadeOverlay', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms ease-out', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('150ms ease-in', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class ActiveThreadComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren(CanvasComponent) canvasComponents?: QueryList<CanvasComponent>;

  // Embedded mode: when provided, use these IDs instead of reading from route query params
  embeddedThreadId = input<string | undefined>();
  embeddedThreadMetadataId = input<string | undefined>();
  embeddedInitialMode = input<AGENT_MODE | null>(null);
  /** Document/contract ID for document chat context — used to load linked documents in the input. */
  documentId = input<string>('');
  documentTypeId = input<string>('');
  projectId = input<string>('');
  /** Compare documents for compare chat context. */
  compareDocuments = input<{ _id: string; name: string }[]>([]);
  /** KPI documents for compare chat context. */
  kpiDocuments = input<{ _id: string; name: string }[]>([]);
  /** Form questions for document-form chat context. */
  formQuestions = input<{ _id: string; name: string; sectionTitle?: string }[]>([]);
  /** When set, auto-selects the matching form question in the tools map. */
  selectedFormQuestionId = input<string | null>(null);
  /** Emitted when the thread is deleted in embedded mode (instead of navigating away). */
  @Output() threadDeleted = new EventEmitter<void>();
  /** Emitted when the active thread's provider is determined from metadata (so parent can sync). */
  @Output() providerChange = new EventEmitter<LLM_PROVIDERS>();
  /** Show a history button in the thread header (used by document-chat embed). */
  showHistoryButton = input<boolean>(false);
  /** Emitted when the history button is clicked. */
  toggleHistoryRequest = output<Event>();

  /** true , active theread we are showing as sidepannel/tabs */
  isSidePannel = input<boolean>(false);
  embeddingsStatus = input<boolean>(true);

  // Services
  private agentService = inject(THREAD_AGENT_SERVICE);
  public agentConfig: IThreadAgentConfig = inject(THREAD_AGENT_CONFIG);
  /** Whether a thread-agent feature is enabled (not in excludedFeatures). */
  isFeatureEnabled = (feature: THREAD_AGENT_FEATURES) => checkFeatureEnabled(this.agentConfig, feature);
  THREAD_AGENT_FEATURES = THREAD_AGENT_FEATURES;
  public threadMessagesService = inject(ThreadMessagesService);
  public toolCallPanelService = inject(ToolCallPanelService);
  public sourcesPanelService = inject(SourcesPanelService);
  public followUpsService = inject(FollowUpsService);
  public sidePanelService = inject(SidePanelService);
  private socketService = inject(SocketService);
  private queryClient = inject(QueryClient);
  private toastService = inject(ToastService);
  private organizationService = inject(OrganizationSetupFormService);
  private userService = inject(UserService);
  private authService = inject(AuthenticationService);
  private blurScreenService = inject(BlurScreenService);
  public userCreditsState = inject(UserCreditsStateService);
  private promptLibraryService = inject(PromptLibraryService);

  // ============================================
  // Save-as-prompt dialog state
  // ============================================

  /** Whether the save-as-prompt editor dialog is open. */
  showSaveAsPromptDialog = signal<boolean>(false);

  /** Form state for the save-as-prompt dialog. */
  saveAsPromptForm = signal<ICreatePromptPayload>({
    title: '',
    content: '',
    groupIds: [],
    accessLevel: 'PERSONAL',
  });

  /**
   * ID of the human message whose approved_enhancement triggered the current
   * save-as-prompt dialog. Null for AI-message saves (no checkpoint tagging needed).
   */
  private _enhancementOriginMessageId: string | null = null;

  /** Whether the save-as-prompt mutation is in flight. */
  isSavingPrompt = signal<boolean>(false);

  /**
   * Group options loaded on demand when the save-as-prompt dialog opens.
   * Passed to `PromptEditorDialogComponent` so the Group multi-select is populated.
   * Typed as `{ label: string; value: string }[]` to satisfy the MultiSelectOption shape
   * without adding an additional import in this file.
   */
  saveAsPromptGroupOptions = signal<{ label: string; value: string }[]>([]);

  // ============================================
  // State - Thread Info
  // ============================================
  threadId = signal<string>('');
  threadMetadataId = signal<string>('');
  thread = signal<IThread | null>(null);
  allThreadMetaData = signal<IThreadMetadata[]>([]);
  currentThreadMetadata = signal<IThreadMetadata | null>(null);
  threadLoading = signal<boolean>(false);
  isOwner = signal<boolean>(true); // false for shared-thread viewers (view-only access)
  permissions = signal<{ canView: boolean; canEdit: boolean; canComment: boolean; restricted: boolean }>({
    canView: false, canEdit: false, canComment: false, restricted: false
  });

  // Subscriptions
  private chatSubscription?: Subscription;
  private threadDeletedSubscription?: Subscription;
  private followUpsSubscription?: Subscription;
  private queryParamsSubscription?: Subscription;
  private destroyRef = inject(DestroyRef);

  // Streaming State
  isSummarizing = signal<boolean>(false);
  /** Task IDs of subagents currently summarizing (multiple can be true at once). */
  summarizingTaskIds = signal<string[]>([]);
  showStreamingErrorOverlay = signal<boolean>(false);
  streamingErrorOverlayAnimating = signal<boolean>(false);
  showCanvasLoading = signal<boolean>(false);

  // Canvas State
  isCanvasPanelOpen = signal<boolean>(false);
  canvasDetails = signal<{ message: MessageItem, isEditable: boolean }>({ message: {} as MessageItem, isEditable: false });
  copyCanvasIcon = 'copy'

  // Todos
  todosDict = signal<Record<string, any[]>>({});
  lastHumanMessageId = signal<string>('');

  // Workspace Mismatch
  workspaceMismatched = signal<boolean>(false);
  private threadWorkspaceId: string = '';
  private currentWorkspace: IWorkspace | null = null;
  private availableWorkspaces: IWorkspace[] = [];

  // Feedback
  showFeedbackDialog = signal<boolean>(false);
  feedbackMessageId = signal<string>('');
  feedbackType = signal<'task_completion' | 'periodic'>('task_completion');

  // ============================================
  // State - AI Model
  // ============================================
  selectedProvider = signal<LLM_PROVIDERS>(LLM_PROVIDERS.ANTHROPIC);

  // ============================================
  // State - Mode
  // ============================================
  currentMode = signal<AGENT_MODE>(AGENT_MODE.ASK);
  currentModeConsumedPct = computed(() =>
    this.currentMode() === AGENT_MODE.AGENTIC
      ? this.userCreditsState.agentConsumedPct()
      : this.userCreditsState.askConsumedPct()
  );
  noCreditsDialogVisible = signal<boolean>(false);
  private noCreditsEffect = effect(() => {
    const mode = this.currentMode();
    const pct = mode === AGENT_MODE.AGENTIC
      ? this.userCreditsState.agentConsumedPct()
      : this.userCreditsState.askConsumedPct();
    if (pct !== null && pct >= 100) {
      this.noCreditsDialogVisible.set(true);
    }
  });

  // ============================================
  // State - Restored Custom Instructions
  // ============================================
  // null = loading/unknown, '' = loaded and no instructions, 'text' = loaded with instructions
  restoredCustomInstructions = signal<string | null>(null);
  private _restoredDraftPrompt = signal<string | null>(null);

  // ============================================
  // State - Restored Input State (attachments + query + mode)
  // ============================================
  // Kept as separate private signals so they can be updated independently:
  // _restoredAttachments is set after loadThread() resolves (async, from pendingFiles API)
  // _restoredQuery/_restoredMode are set synchronously from history.state in ngOnInit
  private _restoredAttachments = signal<ChatAttachment[] | null>(null);
  private _restoredQuery = signal<string | null>(null);
  private _restoredMode = signal<AGENT_MODE | null>(null);
  private _restoredFileItems = signal<FileItem[] | null>(null);
  // Tracks confirmed (non-temp) attachments the user has added this session.
  // Updated via the (attachmentsChange) output so they survive component recreation on resize.
  private _sessionAttachments = signal<ChatAttachment[]>([]);

  // Single computed object passed to ThreadInputComponent as one binding
  restoredInputState = computed<RestoredInputState>(() => ({
    attachments: this._sessionAttachments().length > 0
      ? this._sessionAttachments()
      : this._restoredAttachments(),
    query: this._restoredQuery(),
    mode: this._restoredMode(),
    fileItems: this._restoredFileItems(),
    draftPrompt: this._restoredDraftPrompt()
  }));

  onAttachmentsChange(attachments: ChatAttachment[]): void {
    this._sessionAttachments.set(attachments);
  }

  // ============================================
  // State - Drag Overlay
  // ============================================
  isDragActive = signal<boolean>(false);

  // ============================================
  // Computed - Display Info
  // ============================================
  private threadCacheQuery = injectQuery(() => ({
    queryKey: threadKeys.getThread(this.threadId()),
    queryFn: () => Promise.resolve(this.queryClient.getQueryData(threadKeys.getThread(this.threadId())) ?? null,),
    enabled: !!this.threadId(),
  }));
  private threadFromCache = computed(() => (this.threadCacheQuery.data() as { data?: { thread?: unknown } } | null)?.data?.thread,);
  threadTitle = computed(() => {
    const t = (this.threadFromCache() ?? this.thread()) as { title?: string } | undefined;
    return t?.title || `Thread ${this.threadId()}`;
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private browserAgentService: BrowserAgentService,
  ) {
    // Sync ToolCallPanelService with SidePanelService context
    effect(() => {
      const context = this.sidePanelService.context();
      const activeView = this.sidePanelService.activeView();

      if (activeView === 'TOOL_DETAILS' && context.toolCallId) {
        untracked(() => {
          const currentId = this.toolCallPanelService.currentToolCallId();
          if (currentId !== context.toolCallId) {
            this.toolCallPanelService.currentToolCallId.set(context.toolCallId!);
          }
        });
      }
    });

    // Sync isDocumentPreviewPanelOpen with sidePanelService so that
    // clicking a file from anywhere (e.g. human-message attachment group)
    // also opens the mobile file-preview dialog.
    effect(() => {
      const isOpen = this.sidePanelService.isOpen();
      const activeView = this.sidePanelService.activeView();
      const context = this.sidePanelService.context();
      untracked(() => {
        if (isOpen && activeView === 'FILE_PREVIEW') {
          if (context.file) {
            this.selectedDocumentFile.set(context.file);
            this.selectedDocumentId.set(context.documentId || context.file._id || '');
          }
          this.isDocumentPreviewPanelOpen.set(true);
        }
      });
    });
  }

  // ============================================
  // Lifecycle Hooks
  // ============================================
  ngOnInit(): void {
    this.organizationService.currentWorkspace.subscribe(ws => this.currentWorkspace = ws);
    this.getWorkspaces();

    const savedWidth = localStorage.getItem(this.SIDE_PANEL_WIDTH_KEY);
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      if (!isNaN(width) && width >= 20 && width <= 60) {
        this.sidePanelWidthPercent.set(width);
      }
    }

    // Check for initial message from new chat creation (passed via router state)
    // This MUST happen before loadThread() so the optimistic message is not wiped
    const state = history.state;
    let initialMessageItem: MessageItem | null = null;

    if (state?.initialMessage) {
      const initialMsg = state.initialMessage;
      // Include the user-selected tools (`tools` on ChatMessagePayload, persisted as
      // `toolParams` on the HumanMessage in the LangGraph checkpoint) so that
      // AiMessageComponent.getToolParamsForAiMessage() can render the "Tools +N"
      // chip during streaming. Without this, the chip only appears after a refresh
      // (which loads the HumanMessage with toolParams from the checkpoint).
      initialMessageItem = {
        id: initialMsg.messageId || 'temp-' + Date.now(),
        content: initialMsg.content,
        type: MESSAGE_TYPE.HUMAN,
        createdAt: new Date(),
        additional_kwargs: {
          attachments: initialMsg.attachments,
          ...(initialMsg.tools ? { toolParams: initialMsg.tools } : {})
        },
        status: 'SENT'
      };

      if (state.thread) {
        this.thread.set(state.thread);
      }

      if (state.threadMetadata) {
        this.allThreadMetaData.set(state.threadMetadata);
        let currentMetadata = state.threadMetadata.find((m: any) => m._id === this.threadMetadataId());
        if (!currentMetadata && state.threadMetadata.length > 0) {
          currentMetadata = state.threadMetadata[state.threadMetadata.length - 1];
        }
        if (currentMetadata) {
          this.currentThreadMetadata.set(currentMetadata);
          this.threadMessagesService.setThreadMetadataStatus(currentMetadata.status);
          if (currentMetadata.status === THREAD_METADATA_STATUS.ERROR) {
            this.threadMessagesService.setErrorMessage(currentMetadata.errorMessage);
          }
          if (currentMetadata.status === THREAD_METADATA_STATUS.PAUSED) {
            this.threadMessagesService.setThreadPauseReason(currentMetadata.pauseReason ?? null);
          }
          if (currentMetadata.provider) {
            this.selectedProvider.set(currentMetadata.provider);
            this.providerChange.emit(currentMetadata.provider);
          }
        }
      }

      if (initialMsg.mode) {
        this._restoredMode.set(initialMsg.mode);
      }

      // Keep initialMessage in history state so a page refresh before streaming
      // starts can still show the optimistic message. It is cleared on COMPLETED event.

      // Optimistically show the human message immediately — before any API call
      this.threadMessagesService.setMessages([initialMessageItem]);
      // Set metadata status to PENDING — waiting for a Dramatiq worker to pick up the task
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
      this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.PENDING);
    }

    // File-upload path: restore input state (provider, query, mode, attachments) passed from new-chat
    if (state?.inputState) {
      const { provider, query, mode, attachments, fileItems } = state.inputState;

      // We just created this thread — grant full owner access immediately so the
      // input renders without waiting for loadThread() to fetch permissions from the API.
      this.permissions.set({ canView: true, canEdit: true, canComment: true, restricted: false });
      this.isOwner.set(true);

      // Set provider immediately so the model selector shows the correct value
      // before loadThread() runs (prevents the "stuck on OpenAI" regression)
      if (provider) {
        this.selectedProvider.set(provider);
        this.providerChange.emit(provider);
      }
      if (query) {
        this._restoredQuery.set(query);
      }
      if (mode) {
        this._restoredMode.set(mode);
      }
      // Pre-load attachments so they're visible immediately without waiting for getThread() API
      if (attachments?.length) {
        this._restoredAttachments.set(attachments);
      }
      // Preserve full FileItem metadata (dropdownId/kbId) so re-opening the dialog shows selections
      if (fileItems?.length) {
        this._restoredFileItems.set(fileItems);
      }

      if (state.thread) {
        this.thread.set(state.thread);
      }
      if (state.threadMetadata) {
        this.allThreadMetaData.set(state.threadMetadata);
        let currentMetadata = state.threadMetadata.find((m: any) => m._id === this.threadMetadataId());
        if (!currentMetadata && state.threadMetadata.length > 0) {
          currentMetadata = state.threadMetadata[state.threadMetadata.length - 1];
        }
        if (currentMetadata) {
          this.currentThreadMetadata.set(currentMetadata);
          this.threadMessagesService.setThreadMetadataStatus(currentMetadata.status);
        }
      }
    }

    // Get threadId and threadMetadataId — from inputs (embedded mode) or route query params
    const embThreadId = this.embeddedThreadId();
    const embThreadMetadataId = this.embeddedThreadMetadataId();
    if (embThreadId && embThreadMetadataId) {
      const embInitialMode = this.embeddedInitialMode();
      if (embInitialMode && !this._restoredMode()) {
        this._restoredMode.set(embInitialMode);
      }
      this.threadId.set(embThreadId);
      this.threadMetadataId.set(embThreadMetadataId);
      this.threadMessagesService.setActiveThreadId(embThreadId);
      this.loadThread();
    } else {
      this.queryParamsSubscription = this.route.queryParams.subscribe(params => {
        const newThreadId = params['threadId'] || '';
        const previousThreadId = this.threadId();
        if (previousThreadId && previousThreadId !== newThreadId) {
          this.leaveThreadRoom(previousThreadId);
          this.resetThreadData();
        }

        this.threadId.set(newThreadId);
        this.threadMetadataId.set(params['threadMetadataId'] || '');
        this.threadMessagesService.setActiveThreadId(newThreadId);

        if (this.threadId() && this.threadMetadataId()) {
          // Join the room immediately so socket events are received even if the
          // user sends a message before loadThread()'s API call returns.
          this.joinThreadRoom(this.threadId());
          this.loadThread();
        }
      });
    }

    // Subscribe to socket events for real-time updates
    this.setupSocketListeners();

    this.followUpsSubscription = this.followUpsService.followUpClicked$.pipe(
      throttleTime(400, undefined, { leading: true, trailing: false })
    ).subscribe(question => {
      if (this.threadInputComponent) {
        this.threadInputComponent.setMessageText(question);
        setTimeout(() => {
          if (this.threadInputComponent) {
            this.threadInputComponent.sendMessage();
          }
        }, 100);
      }
    });

  }

  // ============================================
  // Thread Actions
  // ============================================
  /**
   * Send a new message to the existing thread
   */
  sendMessage(payload: ChatMessagePayload): void {
    if (!this.threadId()?.trim() || !this.threadMetadataId()?.trim() || this.workspaceMismatched()) {
      return;
    }
    const messageId = uuidv4();

    // Optimistic update: Add user message immediately.
    // Mirror what the backend persists onto the HumanMessage's additional_kwargs in the
    // LangGraph checkpoint (`toolParams`) so the AI bubble's "Tools +N" chip renders
    // during streaming, not only after a page refresh.
    const userMessage: MessageItem = {
      id: messageId,
      content: payload.query,
      type: MESSAGE_TYPE.HUMAN,
      createdAt: new Date(),
      additional_kwargs: {
        attachments: payload.attachments,
        ...(payload.canvasName ? { canvasName: payload.canvasName } : {}),
        ...(payload.tools && payload.tools.length ? { toolParams: payload.tools } : {}),
      }
    };
    this.threadMessagesService.addMessages([userMessage]);

    // Set status to PENDING while waiting for a Dramatiq worker to pick up the task
    this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
    this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.PENDING);

    // Send via service
    this.agentService.runThread({
      ...payload,
      messageId,
      threadId: this.threadId(),
      threadMetadataId: this.threadMetadataId(),
      ...(this.documentId() ? { documentId: this.documentId() } : {})
    }).then(res => {
      const updatedTitle = (res?.data?.thread as IThread)?.title;
      const cachedTitle = this.threadTitle();
      if (updatedTitle && updatedTitle !== cachedTitle) {
        this.updateThreadTitleCache(this.threadId(), updatedTitle);
      }
    }).catch(err => {
      console.error('Failed to send message', err);
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.ERROR);
    });
  }

  /**
   * Pause the current thread execution
   */
  pauseThread(): void {

    this.agentService.pauseThread(this.threadId(), this.threadMetadataId()).then(() => {
      this.threadMessagesService.setThreadPauseReason('user_paused');
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PAUSED);
    });
  }

  // ============================================
  // Data Loading
  // ============================================
  /**
   * Load thread data and messages
   */
  private async loadThread(): Promise<void> {
    this.restoredCustomInstructions.set(null);
    this._restoredDraftPrompt.set(null);
    this.sidePanelService.reset();
    this.toolCallPanelService.clearToolCalls();

    // When thread data is pre-loaded from history.state (text-send or file-upload navigation),
    // skip the skeleton and don't wipe pre-loaded attachments. The API still runs in the
    // background to fetch permissions, full metadata, pendingFiles, etc.
    // Without pre-loaded data (page refresh, direct URL), show the skeleton as normal.
    const hasPreloadedData = !!(history.state?.initialMessage || history.state?.inputState);
    if (!hasPreloadedData) {
      this._sessionAttachments.set([]);
      this._restoredAttachments.set(null);
      if (this.threadMessagesService.messages().length === 0) {
        this.threadLoading.set(true);
      }
    }

    try {
      const res = await this.agentService.getThread(this.threadId());
      this.queryClient.setQueryData(threadKeys.getThread(this.threadId()), res ?? null);

      // Discard stale response if it belongs to a different thread than the one currently active
      if (res.data?.thread?._id !== this.threadId()) return;

      if (res.data) {
        const mongoMessages = this.dedupePersistedHumanGhosts(
          this.normalizeMessages(res.data.conversation.messages)
        );
        // Update local state
        if (res.data.permissions) {
          this.permissions.set(res.data.permissions);
          if (res.data.permissions.canEdit) {
            this.isOwner.set(res.data.permissions.canEdit)
          } else {
            this.isOwner.set(false)
          }
          if (!res.data.permissions.canView) {
            this.permissions.set({ ...res.data.permissions, restricted: true });
            this.threadLoading.set(false);
            return;
          }
        }
        if (res.data.thread) {
          this.thread.set(res.data.thread);
          this.checkIfSameWorkspace();
        }
        if (res.data.threadMetadata) {
          this.allThreadMetaData.set(res.data.threadMetadata);

          let currentMetadata = res.data.threadMetadata.find((m: any) => m._id === this.threadMetadataId());
          if (!currentMetadata && res.data.threadMetadata.length > 0) {
            currentMetadata = res.data.threadMetadata[res.data.threadMetadata.length - 1];
          }

          if (currentMetadata) {
            this.currentThreadMetadata.set(currentMetadata);
            // Don't overwrite an active status (PENDING/IN_PROGRESS/PAUSED) with a stale
            // INACTIVE from the API response — this can happen in a race condition where
            // the user clicks send before loadThread() completes (e.g. file-upload flow).
            const currentStatus = this.threadMessagesService.threadMetadataStatus();
            const activeStatuses: string[] = [
              THREAD_METADATA_STATUS.PENDING,
              THREAD_METADATA_STATUS.IN_PROGRESS,
              THREAD_METADATA_STATUS.PAUSED,
            ];
            if (!activeStatuses.includes(currentStatus ?? '')) {
              this.threadMessagesService.setThreadMetadataStatus(currentMetadata.status);
            }
            if (currentMetadata.status === THREAD_METADATA_STATUS.ERROR) {
              this.threadMessagesService.setErrorMessage(currentMetadata.errorMessage);
            }
            if (currentMetadata.status === THREAD_METADATA_STATUS.PAUSED) {
              this.threadMessagesService.setThreadPauseReason(currentMetadata.pauseReason ?? null);
            }
            if (currentMetadata.status === THREAD_METADATA_STATUS.INTERRUPTED) {
              // PROMPT_REUSE_SUGGESTION takes priority — check it first so that
              // a reuse interrupt is never mistakenly shown as an enhancement card.
              const pendingReuseCandidates = (res.data as any).pendingReuseCandidates;
              if (Array.isArray(pendingReuseCandidates) && pendingReuseCandidates.length > 0) {
                this.threadMessagesService.setPromptReuseSuggestion(pendingReuseCandidates);
                this.threadMessagesService.setPromptEnhancement(null);
              } else if (res.data.pendingEnhancement?.markdown) {
                // Only fall through to the enhancement card when there are no reuse candidates.
                this.threadMessagesService.setPromptEnhancement({
                  source: PROMPT_ENHANCEMENT_INTERRUPT.SOURCE,
                  markdown: res.data.pendingEnhancement.markdown,
                });
              }
            }
            if (currentMetadata.provider && !history.state?.inputState) {
              this.selectedProvider.set(currentMetadata.provider);
              this.providerChange.emit(currentMetadata.provider);
            }

            const customInstructionsParam = (currentMetadata.toolParams ?? [])
              .find((p: Record<string, any>) => p['CUSTOM_INSTRUCTIONS']);
            this.restoredCustomInstructions.set(
              customInstructionsParam ? customInstructionsParam['CUSTOM_INSTRUCTIONS'] : ''
            );

            this._restoredDraftPrompt.set(currentMetadata.draftPrompt ?? '');

            const storedInputTokens = currentMetadata.inputTokens;
            if (storedInputTokens !== undefined && storedInputTokens !== null) {
              this.threadMessagesService.setInputTokens(storedInputTokens);
            } else {
              this.threadMessagesService.setInputTokens(null);
            }
          }
        }

        // If MongoDB has no messages yet (e.g. user refreshed before the first message
        // was persisted, or thread was just created via file upload), preserve any
        // existing optimistic messages and just join the room.
        if (mongoMessages.length === 0) {
          this._restoredAttachments.set(this.mapPendingFiles(res.data.pendingFiles));
          this.joinThreadRoom(this.threadId());
          return;
        }
        if (res.data.token_limits_agent) {
          this.threadMessagesService.setTokenLimitsAgent(res.data.token_limits_agent);
        }
        this.threadMessagesService.setMessageFeedbacksByMessageId(res.data.messageFeedbacks);

        // Join room so socket events can be received from this point on
        this.joinThreadRoom(this.threadId());

        // The API already returns checkpoint + Redis merged messages (server-side merge).
        // We only need to overlay any in-memory streaming messages that arrived after
        // the API call was made (they are the most current state for in-progress messages).
        const streamingMessages = this.threadMessagesService.messages();
        let allMessages = mongoMessages;
        if (streamingMessages.length > 0) {
          const messageMap = new Map(allMessages.map(m => [m.id, m]));
          for (const msg of streamingMessages) {
            if (msg.id) {
              messageMap.set(msg.id, msg);
            }
          }
          allMessages = Array.from(messageMap.values());
        }

        this.threadMessagesService.setMessages(allMessages);
        this.toolCallPanelService.initializeToolCallsFromMessages(allMessages);
        this.initializeTodos(allMessages);

        // Messages exist → the restored query/attachments were already sent.
        // Clear the restored input state so the input does not re-populate on
        // the next refresh, and clear any pre-populated text shown on this load.
        if (history.state?.inputState) {
          this._restoredQuery.set(null);
          this._restoredMode.set(null);
          this._sessionAttachments.set([]);
          this._restoredAttachments.set(null);
          const { inputState, ...cleanState } = history.state;
          window.history.replaceState(cleanState, '');
        } else {
          this._restoredAttachments.set(this.mapPendingFiles(res.data.pendingFiles));
        }
      } else {
        this._restoredAttachments.set([]);
        this.joinThreadRoom(this.threadId());
      }
    } catch (err) {
      console.error('Failed to load thread', err);
    } finally {
      this.threadLoading.set(false);
    }
  }

  private mapPendingFiles(pendingFiles?: any[]): ChatAttachment[] {
    if (!pendingFiles?.length) return [];
    return pendingFiles.map(f => ({
      _id: f._id,
      docModel: f.docModel,
      name: f.name,
      type: f.type,
      attachmentData: f.attachmentData,
      ...(f.metadata && { metadata: f.metadata }),
    }));
  }

  onApprovePlan() {
    // Capture the exact markdown the user is approving BEFORE we clear the
    // signal.  We echo it back to the backend via `editedPlan.markdown` so
    // PromptEnhancerMiddleware can pin the persisted brief to this exact
    // content — the middleware re-runs on resume and `_generate_markdown` is
    // non-deterministic, so without echoing the user-visible markdown the
    // backend would persist a freshly-regenerated (and therefore different)
    // version of the prompt.
    const approvedMarkdown = (this.threadMessagesService.promptEnhancement()?.markdown ?? '').trim();
    this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
    this.threadMessagesService.setPromptEnhancement(null);
    this.agentService.resumeThread(
      this.threadId(),
      PLAN_FEEDBACK.APPROVE,
      undefined,
      this.selectedProvider(),
      approvedMarkdown ? { markdown: approvedMarkdown } : undefined,
    ).catch(() => {
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.INTERRUPTED);
    });
  }

  // ── Prompt-reuse interrupt handlers ─────────────────────────────────────────

  /**
   * User chose to reuse the saved prompt with the given ID.
   * Resumes the thread with action="use_existing" so the middleware injects
   * that prompt's content as the approved brief, skipping enhancement.
   *
   * Also locally attaches the chosen prompt's content to the latest human
   * message's ``additional_kwargs.approved_enhancement`` so the existing
   * "Enhanced Prompt" accordion (rendered by ``HumanMessageComponent``)
   * appears immediately, matching the flow for AI-generated enhancements.
   * The backend already persists the same payload on the human message so
   * this remains in sync after reload.
   */
  onUseExistingPrompt(promptId: string): void {
    const candidates = this.threadMessagesService.promptReuseSuggestion() ?? [];
    const chosen = candidates.find(c => c.promptId === promptId);

    if (chosen?.content) {
      const messages = this.threadMessagesService.messages();
      for (let i = messages.length - 1; i >= 0; i--) {
        const msg = messages[i];
        if (msg.type === MESSAGE_TYPE.HUMAN) {
          const existingKwargs = (msg.additional_kwargs ?? {}) as Record<string, any>;
          this.threadMessagesService.updateMessage(msg.id, {
            additional_kwargs: {
              ...existingKwargs,
              approved_enhancement: {
                kind: PROMPT_ENHANCEMENT_INTERRUPT.KIND,
                source: PROMPT_ENHANCEMENT_INTERRUPT.SOURCE,
                markdown: chosen.content,
                prompt_id: promptId,
              },
            },
          });
          break;
        }
      }
    }

    this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
    this.threadMessagesService.setPromptReuseSuggestion(null);
    this.agentService.resumeThread(
      this.threadId(),
      undefined,
      undefined,
      this.selectedProvider(),
      undefined,
      undefined,
      { action: 'use_existing', selected_prompt_id: promptId },
    ).catch(() => {
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.INTERRUPTED);
    });
  }

  /**
   * User wants a freshly AI-generated enhancement instead of reusing a saved prompt.
   * Resumes with action="generate_new" so the middleware proceeds to _generate_markdown
   * and then fires the normal PROMPT_ENHANCEMENT interrupt.
   */
  onGenerateNewPrompt(): void {
    this.threadMessagesService.setPromptReuseSuggestion(null);
    // Leave status as INTERRUPTED — the next interrupt (PROMPT_ENHANCEMENT) will update it.
    this.agentService.resumeThread(
      this.threadId(),
      undefined,
      undefined,
      this.selectedProvider(),
      undefined,
      undefined,
      { action: 'generate_new' },
    ).catch(() => {
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.INTERRUPTED);
    });
  }

  onRejectPlan() {
    this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
    this.threadMessagesService.setPromptEnhancement(null);
    this.agentService.resumeThread(this.threadId(), PLAN_FEEDBACK.REJECT, undefined, this.selectedProvider()).catch(() => {
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.INTERRUPTED);
    });
  }

  /**
   * Called when the user edits and approves the enhanced prompt.
   * Always receives the edited Markdown as a string.
   */
  onEditPlan(edited: string) {
    this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
    this.threadMessagesService.setPromptEnhancement(null);

    // Plans are always Markdown — wrap in the shape the backend expects.
    const editedPlan: Record<string, any> = { markdown: edited };

    // IMPORTANT: send PLAN_FEEDBACK.EDIT (NOT APPROVE) so ``BaseAgent._build_resume_input``
    // forwards ``edited_plan`` into the LangGraph resume payload.  Sending APPROVE drops
    // the edits silently and the middleware falls back to the original AI-generated brief.
    this.agentService.resumeThread(
      this.threadId(),
      PLAN_FEEDBACK.EDIT,
      undefined,
      this.selectedProvider(),
      editedPlan,
    ).catch(() => {
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.INTERRUPTED);
    });
  }

  /** User submitted freeform feedback — resume with REVISE so the middleware regenerates the plan. */
  onRevisePlan(feedback: string): void {
    this.agentService.resumeThread(
      this.threadId(),
      PLAN_FEEDBACK.REVISE,
      undefined,
      this.selectedProvider(),
      undefined,
      feedback,
    ).catch(() => {
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.INTERRUPTED);
    });
  }

  onPlanTimerExpired() {
    this.onApprovePlan();
  }

  handleContinueAfterPause() {
    this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
    this.agentService.resumeThread(this.threadId(), undefined, undefined, this.selectedProvider()).catch(() => {
      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PAUSED);
    });
  }

  handleContinueAfterStreamingError() {
    this.streamingErrorOverlayAnimating.set(false);
    setTimeout(() => {
      this.showStreamingErrorOverlay.set(false);

      const tools = this.threadInputComponent?.getSelectedToolsOptions().map(option => option.selectionTree).filter(tree => tree !== undefined) || [];
      const connectors = this.threadInputComponent?.getSelectedConnectors() || [];

      this.sendMessage({
        query: 'continue',
        provider: this.selectedProvider(),
        attachments: [],
        tools,
        connectors,
        mode: this.threadInputComponent?.selectedMode() ?? AGENT_MODE.ASK
      });
    }, 300);
  }

  onScroll(event: Event) {
    // Handle scroll events if needed (e.g., infinite scroll or tracking)
  }

  // ============================================
  // Thread Menu & Dialogs
  // ============================================
  editThreadDialogVisible = signal<boolean>(false);
  threadTitleToEdit = signal<string>('');
  deleteThreadDialogVisible = signal<boolean>(false);

  menuItems = computed<CustomMenuItem[]>(() => {
    const isMobile = this.currentWindowWidth() < this.MOBILE_BREAKPOINT;
    const baseItems: CustomMenuItem[] = this.isOwner() ? [
      {
        icon: 'pencil',
        iconSource: IconSourceEnum.LUCIDE,
        iconSize: IconSizeEnum.SM,
        label: 'Edit',
        callback: () => this.editThread()
      },
      {
        icon: 'trash-2',
        iconSource: IconSourceEnum.LUCIDE,
        iconSize: IconSizeEnum.SM,
        label: 'Delete',
        callback: () => this.confirmDeleteThread(),
        class: 'text-danger'
      }
    ] : [];
    if (isMobile) {
      const mobileExtras: CustomMenuItem[] = [];
      if (this.isOwner() && this.isFeatureEnabled(THREAD_AGENT_FEATURES.SHARE_THREAD)) {
        mobileExtras.push({
          icon: 'share-2',
          iconSource: IconSourceEnum.LUCIDE,
          label: 'Share',
          callback: () => this.shareThreadRef?.openShareDialog()
        });
      }
      if (this.isFeatureEnabled(THREAD_AGENT_FEATURES.THREAD_FILES) && !this.isSidePannel()) {
        mobileExtras.push({
          icon: 'folder-open',
          iconSource: IconSourceEnum.LUCIDE,
          label: 'Attachments',
          callback: () => this.threadFilesRef?.toggleThreadFiles()
        });
      }
      return [...mobileExtras, ...baseItems];
    }
    return baseItems;
  });

  editThread() {
    this.threadTitleToEdit.set(this.threadTitle()); // Or fetch actual title
    this.editThreadDialogVisible.set(true);
  }

  updateThreadTitle() {
    if (!this.threadTitleToEdit()) return;

    this.agentService.updateThreadTitle(this.threadId(), this.threadTitleToEdit()).then(res => {
      if (res.success) {
        const updatedThread = res.data;
        this.editThreadDialogVisible.set(false);
        this.thread.update(t => t ? { ...t, title: updatedThread.title } : t);
        this.updateThreadTitleCache(this.threadId(), updatedThread.title);
      }
    });
  }

  private updateThreadTitleCache(threadId: string, title: string): void {
    this.queryClient.setQueryData(threadKeys.getThread(threadId), (oldData: any) => {
      if (!oldData?.data?.thread) return oldData;
      return {
        ...oldData,
        data: { ...oldData.data, thread: { ...oldData.data.thread, title } }
      };
    });
    if (this.agentConfig.threadListQueryKey) {
      this.queryClient.setQueryData(this.agentConfig.threadListQueryKey, (oldData: any) => {
        if (!oldData?.pages) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => {
            if (!page?.data?.threads) return page;
            return {
              ...page,
              data: {
                ...page.data,
                threads: page.data.threads.map((t: any) =>
                  t._id === threadId ? { ...t, title } : t
                )
              }
            };
          })
        };
      });
    }
  }

  confirmDeleteThread() {
    this.deleteThreadDialogVisible.set(true);
  }

  deleteThread() {
    const threadIdToRemove = this.threadId();
    this.agentService.deleteThread(threadIdToRemove).then(res => {
      if (res.success) {
        this.deleteThreadDialogVisible.set(false);

        this.queryClient.setQueryData(threadKeys.all, (oldData: any) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => {
              if (!page?.data?.threads) return page;
              const hadThread = page.data.threads.some((t: any) => t._id === threadIdToRemove);
              const threads = page.data.threads.filter((t: any) => t._id !== threadIdToRemove);
              return {
                ...page,
                data: {
                  ...page.data,
                  threads,
                  total: hadThread ? Math.max(0, (page.data.total ?? 0) - 1) : page.data.total
                }
              };
            })
          };
        });

        if (this.embeddedThreadId()) {
          this.threadDeleted.emit();
        } else {
          this.router.navigate([this.agentConfig.homeRoute]);
        }
      }
    });
  }


  // ============================================
  // Layout & Panels
  // ============================================
  readonly MOBILE_BREAKPOINT = MOBILE_BREAKPOINT;
  private hostEl = inject(ElementRef);
  private resizeObserver?: ResizeObserver;
  currentWindowWidth = signal<number>(window.innerWidth);
  sidePanelOpen = signal<boolean>(false);
  sidePanelWidthPercent = signal<number>(35);
  desktopMinPanelSizes: number[] = [40, 25];
  desktopIsResizing = signal<boolean>(false);
  private readonly SIDE_PANEL_WIDTH_KEY = 'active-thread-side-panel-width';
  private savedScrollPosition: number = 0;

  /** Panel sizes for z-resizable: [main %, side %]. When closed, side is 0. */
  desktopPanelSizes = computed(() => {
    const isOpen = this.sidePanelService.isOpen() && this.currentWindowWidth() >= this.MOBILE_BREAKPOINT;
    const sideWidth = this.sidePanelWidthPercent();
    return isOpen ? [100 - sideWidth, sideWidth] : [100, 0];
  });

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;
  @ViewChild(ThreadInputComponent) private threadInputComponent?: ThreadInputComponent;
  @ViewChild(ShareThreadComponent) private shareThreadRef?: ShareThreadComponent;
  @ViewChild(ThreadFilesComponent) private threadFilesRef?: ThreadFilesComponent;

  // Side Panel State
  sidePanelContent = computed(() => {
    // This logic handles switching side panel content
    return 'default';
  });

  // Tool Call State
  currentToolCall = computed(() => {
    const context = this.sidePanelService.context();
    const activeView = this.sidePanelService.activeView();

    if (activeView === 'TOOL_DETAILS' && context.toolCallId) {
      // Logic moved to effect to avoid NG0600 error
    }

    const toolCall = this.toolCallPanelService.currentToolCall();
    return toolCall;
  });

  // Tool loading state - show loading when panel is open but no tool call data yet
  isToolLoading = computed(() => {
    const isOpen = this.sidePanelService.isOpen();
    const isToolDetailsView = this.sidePanelService.isToolDetailsView();
    const toolCall = this.currentToolCall();

    // Show loading if the tool panel is open but we don't have tool call data yet
    return isOpen && isToolDetailsView && !toolCall;
  });

  currentToolCallIndex = this.toolCallPanelService.currentIndex;
  totalToolCalls = this.toolCallPanelService.totalCalls;

  isSourcesPanelOpen = this.sourcesPanelService.isOpen;
  currentSource = this.sourcesPanelService.source;

  isDocumentPreviewPanelOpen = signal<boolean>(false);
  isDocumentFullscreenOpen = signal<boolean>(false);
  selectedDocumentFile = signal<any>(null); // Replace any with IAttachment if available
  selectedDocumentId = signal<string>('');
  fileLoading = signal<boolean>(false);

  showDocumentPreviewDialog = computed(
    () =>
      this.sidePanelService.isOpen() &&
      this.sidePanelService.isFilePreviewView() &&
      (this.currentWindowWidth() < this.MOBILE_BREAKPOINT || this.isDocumentFullscreenOpen())
  );

  // Icons
  ChevronLeft = ChevronLeft;
  ChevronRight = ChevronRight;


  toolIcon = computed(() => {
    const toolCall = this.currentToolCall();
    return getToolIcon(toolCall?.name || '');
  });
  currentToolName = computed(() => {
    const toolCall = this.currentToolCall();
    return toolCall?.name || 'Tool Call';
  });


  toggleDesktopSidePanel(): void {
    const isVisible = this.sidePanelService.isOpen();
    if (isVisible) {
      untracked(() => {
        this.sidePanelService.close();
        this.toolCallPanelService.isOpen.set(false);
        this.sourcesPanelService.isOpen.set(false);
        this.isDocumentPreviewPanelOpen.set(false);
      });
      return;
    }
    // If closed, we don't auto-open to any particular view
    // The individual services handle opening with specific views
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect.width ?? window.innerWidth;
      this.currentWindowWidth.set(width);
    });
    this.resizeObserver.observe(this.hostEl.nativeElement);
  }

  onDesktopResizeStart(): void {
    this.desktopIsResizing.set(true);
  }

  onDesktopResizeEnd(event: ZardResizeEvent): void {
    this.desktopIsResizing.set(false);
    const sideWidth = Math.round(event.sizes[1] ?? 35);
    if (sideWidth > 0) {
      const clamped = Math.max(20, Math.min(60, sideWidth));
      this.sidePanelWidthPercent.set(clamped);
      localStorage.setItem(this.SIDE_PANEL_WIDTH_KEY, String(clamped));
      // If user dragged to expand collapsed panel, open it
      if (!this.sidePanelService.isOpen()) {
        this.sidePanelService.open('TOOL_DETAILS', {});
        this.toolCallPanelService.isOpen.set(true);
      }
    }
  }

  private saveScrollPosition(): void {
    if (this.scrollContainer?.nativeElement) {
      this.savedScrollPosition = this.scrollContainer.nativeElement.scrollTop;
    }
  }

  private restoreScrollPosition(): void {
    setTimeout(() => {
      if (this.scrollContainer?.nativeElement) {
        this.scrollContainer.nativeElement.scrollTop = this.savedScrollPosition;
      }
    }, 0);
  }

  // Document Preview Logic
  openFile(file: any) {
    this.selectedDocumentFile.set(file);
    this.selectedDocumentId.set(file._id);
    this.isDocumentPreviewPanelOpen.set(true);
    this.sidePanelService.open('FILE_PREVIEW', { file, documentId: file._id });
  }

  closeDocumentPreview() {
    this.sidePanelService.close();
    this.isDocumentPreviewPanelOpen.set(false);
    this.selectedDocumentFile.set(null);
  }

  onDocumentPreviewDialogHide() {
    this.sidePanelService.close();
    if (this.isDocumentFullscreenOpen()) {
      this.closeDocumentFullscreen();
    }
  }

  openDocumentFullscreen() {
    this.isDocumentFullscreenOpen.set(true);
  }

  closeDocumentFullscreen() {
    this.isDocumentFullscreenOpen.set(false);
  }

  // Canvas & Image Handlers
  previewImageUrl = signal<string>('');

  openImagePreview(url: string) {
    this.previewImageUrl.set(url);
  }

  openCanvasPanel(event: { message: MessageItem, isEditable: boolean }) {
    this.canvasDetails.set(event);
    this.isCanvasPanelOpen.set(true);
    this.sidePanelService.open('CANVAS', { message: event.message, isEditable: event.isEditable });
  }

  copyCanvas() {
    if (!this.canvasComponents) {
      return;
    }
    const isCopySuccess = copyCanvasContent(this.canvasComponents, this.canvasDetails()!.message);
    if (isCopySuccess) {
      this.copyCanvasIcon = 'check-check'
      setTimeout(() => {
        this.copyCanvasIcon = 'copy'
      }, 2000);
    }
  }

  editCanvasWithAi(event: { query: string, selectedCanvasText: string, canvasName: string }) {
    this.sendMessage({
      query: event.query,
      provider: this.selectedProvider(),
      canvasId: (this.canvasDetails()!.message as any)?.response_metadata?.attachmentId || this.canvasDetails()!.message?.additional_kwargs?.['attachmentId'],
      selectedCanvasText: event.selectedCanvasText,
      canvasName: event.canvasName,
      mode: this.threadInputComponent?.selectedMode() ?? AGENT_MODE.ASK
    });
  }

  downloadCanvas(message: MessageItem) {
    const attachmentId = message['response_metadata']?.attachmentId
    const versionId = message['response_metadata']?.versionId
    this.agentService.downloadCanvas?.(attachmentId, versionId)?.then(res => {
      if (res?.success && res?.data) {
        const blob = new Blob([new Uint8Array(res.data.data)], { type: 'text/markdown;charset=utf-8' });
        saveAs(blob, message['additional_kwargs']?.canvasName);
      }
    });
  }

  downloadDocument() {
    // Implement download logic
  }

  // Tool Navigation
  navigateToPreviousToolCall() {
    this.toolCallPanelService.navigateToPrevious();
  }

  navigateToNextToolCall() {
    this.toolCallPanelService.navigateToNext();
  }

  onToolCallSliderChange(index: number) {
    const calls = this.toolCallPanelService.toolCalls();
    if (calls[index]) {
      this.toolCallPanelService.navigateToToolCall(calls[index].id);
    }
  }

  // Tool Call Panel - close the side panel when X button is clicked (tool/artifact details view)
  toggleToolCallSidePanel() {
    if (this.sidePanelService.isOpen() && this.sidePanelService.isToolDetailsView()) {
      this.sidePanelService.close();
      this.toolCallPanelService.isOpen.set(false);
    }
  }

  toggleSourcesPanel() {
    this.sourcesPanelService.isOpen.update(v => !v);
  }



  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    this.cleanupAndClearState();
  }

  // ============================================
  // Socket Logic
  // ============================================

  async joinThreadRoom(threadId: string) {
    this.socketService.registerRoom('thread_room', CLIENT_EVENTS.JOIN_THREAD_ROOM, threadId);
    const socket = await this.socketService.getSocket();
    if (!socket) {
      return;
    }
    socket.emit(CLIENT_EVENTS.JOIN_THREAD_ROOM, threadId);
  }

  async leaveThreadRoom(threadId: string) {
    this.socketService.unregisterRoom('thread_room');
    const socket = await this.socketService.getSocket();
    if (!socket) {
      return;
    }
    socket.emit(CLIENT_EVENTS.LEAVE_THREAD_ROOM, threadId);
  }

  private updateSidebarAgentStatus(status: THREAD_METADATA_STATUS): void {
    if (!this.agentConfig.threadListQueryKey) return;
    this.queryClient.setQueryData(this.agentConfig.threadListQueryKey, (oldData: any) => {
      if (!oldData || !oldData.pages) return oldData;
      const updatedPages = oldData.pages.map((page: any) => {
        if (!page?.data?.threads) return page;
        return {
          ...page,
          data: {
            ...page.data,
            threads: page.data.threads.map((t: any) =>
              t._id === this.threadId()
                ? { ...t, threadStatus: status }
                : t
            )
          }
        };
      });
      return { ...oldData, pages: updatedPages };
    });
  }

  private resetThreadData(): void {
    this.todosDict.set({});
    this.lastHumanMessageId.set('');
    this.thread.set(null);
    this.allThreadMetaData.set([]);
    this.currentThreadMetadata.set(null);
    this.threadLoading.set(false);
    this.isSummarizing.set(false);
    this.showStreamingErrorOverlay.set(false);
    this._sessionAttachments.set([]);
    this._restoredAttachments.set(null);
    this._restoredQuery.set(null);
    this._restoredMode.set(null);
    this.streamingErrorOverlayAnimating.set(false);
    this.showCanvasLoading.set(false);
    this.isCanvasPanelOpen.set(false);
    this.sidePanelService.reset();
    this.toolCallPanelService.clearToolCalls();
    this.threadMessagesService.clearMessages();
  }

  cleanupAndClearState() {
    this.resetThreadData();
    this.leaveThreadRoom(this.threadId());
    if (this.chatSubscription) {
      this.chatSubscription.unsubscribe();
    }
    if (this.threadDeletedSubscription) {
      this.threadDeletedSubscription.unsubscribe();
    }
    if (this.followUpsSubscription) {
      this.followUpsSubscription.unsubscribe();
    }
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
  }

  private setupSocketListeners() {
    this.chatSubscription = this.socketService.on<any[]>(
      this.agentConfig.chatSocketEvent
    ).subscribe(res => {
      const data: any = res[0];
      if (data.threadId === this.threadId() && data.threadMetadataId === this.threadMetadataId()) {

        if (data.status === THREAD_STREAMING_STATUS.STREAMING_STARTED ||
          data.status === THREAD_STREAMING_STATUS.IN_PROGRESS) {
          const currentStatus = this.threadMessagesService.threadMetadataStatus();
          if (currentStatus === THREAD_METADATA_STATUS.PENDING ||
            currentStatus === THREAD_METADATA_STATUS.INACTIVE ||
            currentStatus === THREAD_METADATA_STATUS.PAUSED) {
            this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.IN_PROGRESS);
            this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.IN_PROGRESS);
          }
        }

        if (data.status === THREAD_STREAMING_STATUS.SUMMARIZING) {
          const taskId = this.resolveSummarizingTaskId(data);
          if (taskId) {
            this.summarizingTaskIds.update(ids => ids.includes(taskId) ? ids : [...ids, taskId]);
          } else {
            this.isSummarizing.set(true);
          }
          return;
        }

        if (data.status === THREAD_STREAMING_STATUS.SUMMARIZATION_COMPLETE) {
          const taskId = this.resolveSummarizingTaskId(data);
          if (taskId) {
            this.summarizingTaskIds.update(ids => ids.filter(id => id !== taskId));
          } else {
            this.isSummarizing.set(false);
          }
        }

        if (data.token_data != null && data.token_data.inputTokens != null) {
          this.threadMessagesService.setInputTokens(data.token_data.inputTokens);
        }

        if (data.status === THREAD_STREAMING_STATUS.ERROR) {
          const errorMessage = typeof data.error_message === 'string' ? data.error_message : undefined;
          this.threadMessagesService.setErrorMessage(errorMessage);
          const currentMetadata = this.currentThreadMetadata();
          if (currentMetadata) {
            this.currentThreadMetadata.set({
              ...currentMetadata,
              status: THREAD_METADATA_STATUS.ERROR
            });
            this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.ERROR);
          }
          this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.ERROR);

          const currentMessages = this.threadMessagesService.messages();
          const errorMsgIndex = currentMessages.findIndex(m =>
            m.id && data.messages?.some((msg: any) => msg.id === m.id)
          );

          if (errorMsgIndex !== -1) {
            currentMessages[errorMsgIndex] = {
              ...currentMessages[errorMsgIndex],
              status: 'ERROR'
            };
            this.threadMessagesService.setMessages([...currentMessages]);
          }

          this.showStreamingErrorOverlay.set(true);
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.streamingErrorOverlayAnimating.set(true);
            });
          });
          this.showCanvasLoading.set(false);
          return;
        }

        if (data.status === THREAD_STREAMING_STATUS.TOOL_ARG_STREAMING) {
          const toolCalls = data.tool_calls || [];
          const messageId = data.messageId;

          if (!messageId || toolCalls.length === 0) return;

          this.threadMessagesService.updateStreamingToolCalls(toolCalls);

          const existingMsg = this.threadMessagesService.messages().find(m => m.id === messageId);
          const hasToolCallsAlready = existingMsg?.tool_calls && existingMsg.tool_calls.length > 0;

          if (!hasToolCallsAlready) {
            const normalizedToolCalls = toolCalls
              .filter((tc: any) => tc.name)
              .map((tc: any) => ({
                id: tc.id || '',
                name: tc.name || '',
                args: {},
                type: 'function'
              }));

            // Namespace may be at the top-level data.namespace OR inside each tool_call item.
            const effectiveBatchNamespace: string[] | undefined =
              data.namespace ?? (toolCalls as any[]).find((tc: any) => tc.namespace)?.namespace;
            const batchIsSubagent = isSubagentNamespace(effectiveBatchNamespace);

            if (existingMsg) {
              // AI message exists (from STREAMING_STARTED) but has no tool_calls yet — patch it
              this.threadMessagesService.updateMessage(messageId, {
                tool_calls: normalizedToolCalls
              });
            } else {
              // No AI message exists yet — create one
              this.updateOrAddMessages([{
                id: messageId,
                type: MESSAGE_TYPE.AI,
                content: '',
                tool_calls: normalizedToolCalls,
                namespace: effectiveBatchNamespace,
                isSubagent: batchIsSubagent
              } as MessageItem]);
            }

            if (this.threadMessagesService.threadMetadataStatus() === THREAD_METADATA_STATUS.IN_PROGRESS) {
              const placeholders: MessageItem[] = normalizedToolCalls
                .filter((toolCall: any) => {
                  const toolName = toolCall.name || '';
                  if ([TOOLS.WRITE_FILE, TOOLS.EDIT_FILE].includes(toolName) && toolCall.args?.file_group === FILE_GROUP.CANVAS) {
                    return false;
                  }
                  return toolName && ![TOOLS.WRITE_TODOS, TOOLS.PRESENT_FILES, TOOLS.TASK].includes(toolName as TOOLS);
                })
                .map((tc: any) => ({
                  id: `${tc.id}-loading`,
                  type: MESSAGE_TYPE.TOOL,
                  tool_call_id: tc.id,
                  name: tc.name,
                  content: '',
                  status: 'loading',
                  namespace: effectiveBatchNamespace,
                  isSubagent: batchIsSubagent,
                  additional_kwargs: { metadata: { toolName: tc.name } }
                } as MessageItem));

              if (placeholders.length > 0) {
                this.insertPlaceholdersAfterAiMessage(messageId, placeholders);
              }
            }
          }
          return;
        }

        if (data.messages && data.messages.length > 0) {
          const normalizedMessages = this.normalizeMessages(data.messages || [], data.namespace);

          // First insert/update the actual messages (AI messages with content must land in place first)
          this.updateOrAddMessages(normalizedMessages);

          // Then insert placeholder tool messages RIGHT AFTER their parent AI message
          for (const message of normalizedMessages) {
            if (message.type === MESSAGE_TYPE.AI && message.tool_calls && message.tool_calls.length > 0) {
              this.threadMessagesService.clearStreamingToolCalls();
              if (!this.showCanvasLoading()) {
                for (const toolCall of message.tool_calls) {
                  if ([TOOLS.WRITE_FILE, TOOLS.EDIT_FILE].includes(toolCall.name) && toolCall.args?.file_group === FILE_GROUP.CANVAS) {
                    this.showCanvasLoading.set(true);
                    break;
                  }
                }
              }

              if (this.threadMessagesService.threadMetadataStatus() === THREAD_METADATA_STATUS.IN_PROGRESS) {
                // Propagate subagent identity from the AI message so the placeholder is correctly
                // identified as belonging to the task group in the lookahead grouping logic.
                const effectiveNamespace: string[] | undefined =
                  message.namespace ?? message.additional_kwargs?.['namespace'] ?? data.namespace;
                const messageIsSubagent = isSubagentNamespace(effectiveNamespace);
                const placeholderToolMessages: MessageItem[] = message.tool_calls
                  .filter((toolCall: any) => {
                    const toolName = toolCall.name || '';
                    if ([TOOLS.WRITE_FILE, TOOLS.EDIT_FILE].includes(toolName) && toolCall.args?.file_group === FILE_GROUP.CANVAS) {
                      return false;
                    }
                    return toolName && ![TOOLS.WRITE_TODOS, TOOLS.PRESENT_FILES, TOOLS.TASK].includes(toolName as TOOLS);
                  })
                  .map((toolCall: any) => {
                    const toolName = toolCall.name || toolCall.function?.name || '';
                    const placeholder: MessageItem = {
                      id: `${toolCall.id}-loading`,
                      type: MESSAGE_TYPE.TOOL,
                      tool_call_id: toolCall.id,
                      name: toolName,
                      content: '',
                      status: 'loading',
                      additional_kwargs: {
                        metadata: {
                          toolName: toolName
                        }
                      }
                    } as MessageItem;
                    if (messageIsSubagent) {
                      (placeholder as any).isSubagent = true;
                      if (effectiveNamespace) {
                        (placeholder as any).namespace = effectiveNamespace;
                      }
                    }
                    return placeholder;
                  });

                if (placeholderToolMessages.length > 0) {
                  // Insert placeholders immediately after the AI message in the current list
                  this.insertPlaceholdersAfterAiMessage(message.id, placeholderToolMessages);
                }
              }
            }

            // Check if a canvas tool has completed (must be at outer loop level to catch TOOL messages)
            if (this.showCanvasLoading() && message.type === MESSAGE_TYPE.TOOL && message['response_metadata']?.isCanvas) {
              this.showCanvasLoading.set(false);
              if (this.isCanvasPanelOpen()) {
                this.canvasDetails.set({ message: message as MessageItem, isEditable: true });
              }
            }
          }
        }

        if (data.status === THREAD_STREAMING_STATUS.PAUSED) {
          this.threadMessagesService.setThreadPauseReason((data as any).pauseReason ?? null);
          this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PAUSED);
          this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.PAUSED);
        }
        if (data.status === THREAD_STREAMING_STATUS.INTERRUPTED) {
          this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.INTERRUPTED);
          this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.INTERRUPTED);

          const interrupt = (data as any).interrupt;

          // Check for PROMPT_REUSE_SUGGESTION first — it fires before PROMPT_ENHANCEMENT.
          if (
            interrupt?.kind === PROMPT_REUSE_INTERRUPT.KIND &&
            interrupt?.source === PROMPT_REUSE_INTERRUPT.SOURCE &&
            Array.isArray(interrupt?.candidates)
          ) {
            this.threadMessagesService.setPromptReuseSuggestion(interrupt.candidates);
            this.threadMessagesService.setPromptEnhancement(null);
          } else if (
            // Only the Markdown enhanced-prompt payload from
            // PromptEnhancerMiddleware triggers the confirmation card. Todo-list
            // interrupts fall through to the generic interrupt UI.
            interrupt?.kind === PROMPT_ENHANCEMENT_INTERRUPT.KIND &&
            interrupt?.source === PROMPT_ENHANCEMENT_INTERRUPT.SOURCE &&
            interrupt?.plan?.markdown
          ) {
            this.threadMessagesService.setPromptEnhancement({
              source: PROMPT_ENHANCEMENT_INTERRUPT.SOURCE,
              markdown: interrupt.plan.markdown,
            });
            this.threadMessagesService.setPromptReuseSuggestion(null);
          } else {
            // Generic or todo interrupt — clear both enhancement cards.
            this.threadMessagesService.setPromptEnhancement(null);
            this.threadMessagesService.setPromptReuseSuggestion(null);
          }
        }

        if (data.status === THREAD_STREAMING_STATUS.COMPLETED) {
          this.threadMessagesService.clearStreamingToolCalls();
          this.threadMessagesService.setPromptEnhancement(null);
          this.threadMessagesService.setPromptReuseSuggestion(null);
          const currentMetadata = this.currentThreadMetadata();
          if (currentMetadata) {
            this.currentThreadMetadata.set({
              ...currentMetadata,
              status: THREAD_METADATA_STATUS.COMPLETED
            });
          }
          this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.COMPLETED);
          this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.COMPLETED);
          this.userCreditsState.refreshCredits().subscribe();

          // Streaming is done — remove initialMessage and inputState from history state so
          // that navigating back or refreshing loads fresh data instead of the optimistic/
          // pre-populated state.
          if (history.state?.initialMessage || history.state?.inputState) {
            const { initialMessage, inputState, thread, threadMetadata, ...cleanState } = history.state;
            window.history.replaceState(cleanState, '');
          }

          this.streamingErrorOverlayAnimating.set(false);
          this.showStreamingErrorOverlay.set(false);

          if (data.generated_files && data.generated_files.length > 0) {
            const currentMessages = this.threadMessagesService.messages();
            const lastAiMessageIndex = [...currentMessages].reverse().findIndex(m => m.type === MESSAGE_TYPE.AI);
            if (lastAiMessageIndex !== -1) {
              const actualIndex = currentMessages.length - 1 - lastAiMessageIndex;
              const updatedMessages = [...currentMessages];
              updatedMessages[actualIndex] = {
                ...currentMessages[actualIndex],
                additional_kwargs: {
                  ...currentMessages[actualIndex].additional_kwargs,
                  generated_files: data.generated_files
                }
              };
              this.threadMessagesService.setMessages(updatedMessages);
            }
          }

        }
      }
    });

    this.threadDeletedSubscription = this.socketService.on<{ threadId: string; message: string }>(
      this.agentConfig.threadDeletedSocketEvent
    ).subscribe(res => {
      const data = res[0];
      if (data && data.threadId === this.threadId()) {
        this.cleanupAndClearState();
        this.router.navigate([this.agentConfig.homeRoute]);
      }
    });

  }

  /**
   * Inserts placeholder tool messages immediately after the AI message that triggered them.
   * This ensures that tool placeholders are always in the correct position in the messages array,
   * preventing tool groups from incorrectly spanning across an AI message with content.
   */
  private insertPlaceholdersAfterAiMessage(aiMessageId: string, placeholders: MessageItem[]): void {
    const currentMessages = this.threadMessagesService.messages();
    const aiIndex = currentMessages.findIndex(m => m.id === aiMessageId);

    // Only insert placeholders that don't already exist (by tool_call_id)
    const existingToolCallIds = new Set(
      currentMessages
        .filter(m => m.type === MESSAGE_TYPE.TOOL && m.tool_call_id)
        .map(m => m.tool_call_id)
    );
    const newPlaceholders = placeholders.filter(p => !existingToolCallIds.has(p.tool_call_id));

    if (newPlaceholders.length === 0) return;

    const updatedMessages = [...currentMessages];
    if (aiIndex !== -1) {
      // Insert right after the AI message
      updatedMessages.splice(aiIndex + 1, 0, ...newPlaceholders);
    } else {
      // AI message not found (edge case), append
      updatedMessages.push(...newPlaceholders);
    }

    this.threadMessagesService.setMessages(updatedMessages);
    this.toolCallPanelService.initializeToolCallsFromMessages(updatedMessages);
    this.initializeTodos(updatedMessages);
  }

  private updateOrAddMessages(newMessages: any[]): void {
    const currentMessages = this.threadMessagesService.messages();
    let messagesChanged = false;
    const normalizeContent = (str: string) => str?.trim()?.replace(/\s+/g, ' ').toLowerCase() || '';

    for (const newMsg of newMessages) {
      const existingIndex = currentMessages.findIndex(m => m.id === newMsg.id);
      if (existingIndex !== -1) {
        const oldContent = currentMessages[existingIndex].content;
        const newContent = newMsg.content;
        const existingAttachments = currentMessages[existingIndex].additional_kwargs?.generated_files;
        const newAttachments = newMsg.additional_kwargs?.generated_files;
        const hasNewAttachments = newAttachments?.length && (!existingAttachments?.length || newAttachments.length !== existingAttachments.length);
        const existingPresentedFiles = currentMessages[existingIndex].additional_kwargs?.presented_files;
        const newPresentedFiles = newMsg.additional_kwargs?.presented_files;
        const hasNewPresentedFiles = newPresentedFiles?.length && (!existingPresentedFiles?.length || newPresentedFiles.length !== existingPresentedFiles.length);

        // Detect a freshly-added or revised approved_enhancement on a HumanMessage.
        // The PromptEnhancerMiddleware re-emits the original human message with the
        // SAME id and content but adds ``additional_kwargs.approved_enhancement``;
        // without this check the merge below short-circuits and the "Enhanced
        // Prompt" accordion never renders until the next page refresh.
        const incomingApprovedEnh: any = newMsg.additional_kwargs?.approved_enhancement;
        const existingApprovedEnh: any = currentMessages[existingIndex].additional_kwargs?.approved_enhancement;
        const hasNewApprovedEnhancement = !!incomingApprovedEnh && (
          !existingApprovedEnh ||
          existingApprovedEnh.markdown !== incomingApprovedEnh.markdown ||
          (existingApprovedEnh.prompt_id ?? null) !== (incomingApprovedEnh.prompt_id ?? null)
        );

        const hasNewAdditionalKwargs =
          !!newMsg.additional_kwargs?.is_final_response && !currentMessages[existingIndex].additional_kwargs?.is_final_response ||
          !!newMsg.additional_kwargs?.is_followup && !currentMessages[existingIndex].additional_kwargs?.is_followup ||
          hasNewApprovedEnhancement;

        if (oldContent !== newContent || currentMessages[existingIndex].status !== newMsg.status || hasNewAttachments || hasNewPresentedFiles || hasNewAdditionalKwargs) {
          // During streaming, a subsequent socket event may send the same message with empty content,
          // which would cause the AI message to flash visible then disappear into the tool group.
          // Preserve non-empty existing content so it is never wiped out by an empty incoming update.
          const preservedContent = (oldContent && !newContent) ? oldContent : newContent;
          currentMessages[existingIndex] = {
            ...currentMessages[existingIndex],
            ...newMsg,
            content: preservedContent,
            additional_kwargs: {
              ...currentMessages[existingIndex].additional_kwargs,
              ...newMsg.additional_kwargs
            },
            status: newMsg.status || currentMessages[existingIndex].status
          };
          messagesChanged = true;
        }
        continue;
      }

      if (newMsg.type === MESSAGE_TYPE.TOOL && newMsg.tool_call_id) {
        const placeholderIndex = currentMessages.findIndex(m => m.type === MESSAGE_TYPE.TOOL && m.tool_call_id === newMsg.tool_call_id && m.id.endsWith('-loading'));
        if (placeholderIndex !== -1) {
          currentMessages[placeholderIndex] = {
            ...currentMessages[placeholderIndex],
            ...newMsg,
            additional_kwargs: {
              ...currentMessages[placeholderIndex].additional_kwargs,
              ...newMsg.additional_kwargs
            }
          };
          messagesChanged = true;
          continue;
        }
      }

      if (newMsg.type === MESSAGE_TYPE.HUMAN) {
        const duplicateIndex = currentMessages.findIndex(m => m.type === MESSAGE_TYPE.HUMAN && normalizeContent(m.content) === normalizeContent(newMsg.content) && m.id !== newMsg.id && (m.status === 'SENDING' || m.status === 'SENT'));
        if (duplicateIndex !== -1) {
          currentMessages[duplicateIndex] = {
            ...currentMessages[duplicateIndex],
            ...newMsg,
            status: newMsg.status || 'SENT'
          };
          messagesChanged = true;
          continue;
        }
      }

      if (newMsg.type === MESSAGE_TYPE.AI) {
        const aiDuplicateIndex = currentMessages.findIndex(m => m.type === MESSAGE_TYPE.AI && m.id !== newMsg.id && normalizeContent(m.content) === normalizeContent(newMsg.content) && m.content.length > 50);
        if (aiDuplicateIndex !== -1) {
          continue;
        }
      }

      // For tool results with no matching placeholder: insert after the last tool message
      // that belongs to the same parent AI message, keeping tool results co-located with
      // their AI message (critical for subagent grouping).
      // EXCEPTION: task tool results must append at the end so that pruning can find and
      // remove intermediate subagent messages between the parent AI and the result.
      if (newMsg.type === MESSAGE_TYPE.TOOL && newMsg.tool_call_id) {
        const parentAiIndex = currentMessages.findIndex(
          m => m.type === MESSAGE_TYPE.AI &&
            m.tool_calls?.some((tc: any) => tc.id === newMsg.tool_call_id)
        );
        if (parentAiIndex !== -1) {
          const isTaskResult = currentMessages[parentAiIndex].tool_calls?.some(
            (tc: any) => tc.id === newMsg.tool_call_id && tc.name === TOOLS.TASK
          );
          if (!isTaskResult) {
            let insertAfterIndex = parentAiIndex;
            for (let k = parentAiIndex + 1; k < currentMessages.length; k++) {
              const sibling = currentMessages[k];
              if (sibling.type === MESSAGE_TYPE.TOOL &&
                currentMessages[parentAiIndex].tool_calls?.some((tc: any) => tc.id === sibling.tool_call_id)) {
                insertAfterIndex = k;
              } else {
                break;
              }
            }
            currentMessages.splice(insertAfterIndex + 1, 0, { ...newMsg, status: newMsg.status || 'SENT' });
            messagesChanged = true;
            continue;
          }
        }
      }

      currentMessages.push({ ...newMsg, status: newMsg.status || 'SENT' });
      messagesChanged = true;
    }

    /* --- Summarization message reordering (streaming scenario) ---
    During streaming, when the context limit is approached, the backend emits a summarization
    message AFTER the message that triggered it. But it gets checkpointed BEFORE the message that triggered it.
    So, to maintain consistency, we reorder the summarization message to appear before the message that triggered it.
    We only reorder main-thread summarization messages. Because for now, subagent messages are not being checkpointed.*/

    const lastMsg = currentMessages[currentMessages.length - 1] as MessageItem;
    const isMainThreadSummarizationAtEnd =
      currentMessages.length >= 2 &&
      isSummarizationMessage(lastMsg) &&
      !isSubagentNamespace(lastMsg.additional_kwargs?.['namespace']);

    if (isMainThreadSummarizationAtEnd) {
      const summarizationMsg = currentMessages[currentMessages.length - 1];
      const msgBeforeSummarization = currentMessages[currentMessages.length - 2];

      if (msgBeforeSummarization.type === MESSAGE_TYPE.TOOL && msgBeforeSummarization.tool_call_id) {
        // The summarization message landed after a tool result. Find the parent AI message
        // that owns this tool call so we can insert the summarization before the whole tool group.
        const parentAiIndex = currentMessages.findIndex(
          m => m.type === MESSAGE_TYPE.AI &&
            (m.tool_calls ?? []).some((tc: any) => tc.id === msgBeforeSummarization.tool_call_id)
        );

        if (parentAiIndex !== -1) {
          // Move summarization to just before the parent AI message, pushing the tool group after it
          currentMessages.splice(currentMessages.length - 1, 1);
          currentMessages.splice(parentAiIndex, 0, summarizationMsg);
        } else {
          // Parent AI not found — simply swap summarization above the tool result
          currentMessages[currentMessages.length - 2] = summarizationMsg;
          currentMessages[currentMessages.length - 1] = msgBeforeSummarization;
        }
      } else {
        // Previous message is not a tool result — swap so summarization renders before it
        currentMessages[currentMessages.length - 2] = summarizationMsg;
        currentMessages[currentMessages.length - 1] = msgBeforeSummarization;
      }

      messagesChanged = true;
    }

    // After all messages are merged, prune intermediate subagent messages for completed tasks.
    // A task is considered complete when a tool result message arrives whose tool_call_id matches
    // a `task` tool call on a parent AI message AND the result has non-empty content.
    // The server may or may not set `name: 'task'` on the tool result directly, so we identify
    // it via the parent AI message lookup rather than relying on msg.name.
    for (const msg of newMessages) {
      if (
        msg.type === MESSAGE_TYPE.TOOL &&
        msg.content?.trim() &&
        msg.tool_call_id
      ) {
        const taskCallId = msg.tool_call_id;
        // Verify the parent AI message has a `task` tool call with this ID
        const parentAiIndex = currentMessages.findIndex(
          m => m.type === MESSAGE_TYPE.AI &&
            m.tool_calls?.some((tc: any) => tc.id === taskCallId && tc.name === TOOLS.TASK)
        );
        if (parentAiIndex === -1) continue;

        const taskResultIndex = currentMessages.findIndex(
          m => m.type === MESSAGE_TYPE.TOOL && m.tool_call_id === taskCallId && m.id === msg.id
        );

        if (taskResultIndex !== -1 && taskResultIndex > parentAiIndex + 1) {
          // Remove all subagent messages strictly between parent AI and task result,
          // and also any subagent messages that arrived after the task result.
          const isSubagentMsg = (m: MessageItem) => {
            const ns = (m as any).namespace ?? m.additional_kwargs?.['namespace'];
            return isSubagentNamespace(ns);
          };

          const before = currentMessages.slice(0, parentAiIndex + 1);
          const between = currentMessages.slice(parentAiIndex + 1, taskResultIndex);
          const after = currentMessages.slice(taskResultIndex);

          const filteredBetween = between.filter(m => !isSubagentMsg(m));
          // Keep the task result itself (index 0), strip any trailing subagent messages
          const filteredAfter = [after[0], ...after.slice(1).filter(m => !isSubagentMsg(m))];

          if (filteredBetween.length !== between.length || filteredAfter.length !== after.length) {
            currentMessages.length = 0;
            currentMessages.push(...before, ...filteredBetween, ...filteredAfter);
            messagesChanged = true;
          }
        }
      }
    }

    if (messagesChanged) {
      this.threadMessagesService.setMessages([...currentMessages]);
      this.toolCallPanelService.initializeToolCallsFromMessages(this.threadMessagesService.messages());
      this.initializeTodos(this.threadMessagesService.messages());
    }
  }

  /** Resolve subagent task ID from SUMMARIZING / SUMMARIZATION_COMPLETE payload via namespace. */
  private resolveSummarizingTaskId(data: any): string | null {
    if (data?.['additional_kwargs']?.['namespace']) {
      return getSubagentTaskId(data?.['additional_kwargs']?.['namespace']);
    }
    return null;
  }

  private normalizeMessages(messages: any[], namespace?: string[]): any[] {
    return messages.map(msg => this.normalizeMessage(msg, namespace));
  }

  /**
   * Deduplicates human messages that the backend sometimes returns twice for a freshly
   * created thread: once from the LangGraph checkpoint state (without `createdAt`) and
   * once from the persisted Mongo conversation (with `createdAt`). Both share the same
   * content but carry different server-generated ids, so id-based dedupe elsewhere cannot
   * collapse them. We drop the checkpoint-state copy whenever a persisted copy with the
   * same content exists.
   *
   * @param messages Normalized messages from the backend response.
   * @returns Messages with ghost human duplicates removed.
   */
  private dedupePersistedHumanGhosts(messages: any[]): any[] {
    const persistedHumanContents = new Set<string>();
    for (const m of messages) {
      if (m?.type === MESSAGE_TYPE.HUMAN && m?.createdAt) {
        persistedHumanContents.add(String(m.content ?? ''));
      }
    }
    if (persistedHumanContents.size === 0) return messages;
    return messages.filter(m => {
      if (m?.type !== MESSAGE_TYPE.HUMAN) return true;
      if (m?.createdAt) return true;
      return !persistedHumanContents.has(String(m.content ?? ''));
    });
  }

  private normalizeMessage(msg: any, batchNamespace?: string[]): any {
    let content = msg.content;
    if (Array.isArray(content)) {
      const textParts = content.filter(block => (typeof block === 'string' || block?.type === 'text')).map(block => (typeof block === 'string' ? block : block.text || block.content || ''));
      content = textParts.join(' ').trim();
    }
    if (content && typeof content === 'object' && !Array.isArray(content)) {
      content = content.text || content.content || JSON.stringify(content);
    }
    if (typeof content !== 'string') {
      content = String(content || '');
    }
    let toolCalls = msg.tool_calls || [];
    if (!Array.isArray(toolCalls)) toolCalls = [];
    toolCalls = toolCalls.map((tc: any) => ({
      id: tc.id || tc.tool_call_id || '',
      name: tc.name || tc.function?.name || '',
      args: tc.args || tc.function?.arguments || {},
      type: tc.type || 'function'
    }));
    const normalized = {
      ...msg,
      id: msg.id || msg._id || '',
      content,
      tool_calls: toolCalls
    };
    // Resolve namespace: batch-level > message-level additional_kwargs > message top-level
    const effectiveNamespace: string[] | undefined =
      batchNamespace ??
      msg.additional_kwargs?.['namespace'] ??
      msg.namespace;
    if (effectiveNamespace) {
      normalized.namespace = effectiveNamespace;
    }
    if (isSubagentNamespace(effectiveNamespace)) {
      normalized.isSubagent = true;
    }
    return normalized;
  }

  initializeTodos(messages: MessageItem[]) {
    this.todosDict.set({});
    let prevHumanMsgId = '';
    for (const message of messages) {
      if (message.type === MESSAGE_TYPE.HUMAN) {
        prevHumanMsgId = message.id;
        this.lastHumanMessageId.set(message.id);
      } else if (message.type === MESSAGE_TYPE.AI && message.tool_calls?.length) {
        for (const toolCall of message.tool_calls) {
          if (toolCall.name === TOOLS.WRITE_TODOS) {
            if (prevHumanMsgId) {
              this.todosDict.update(dict => ({
                ...dict,
                [prevHumanMsgId]: toolCall.args.todos || []
              }));
            }
          }
        }
      }
    }
  }
  handleMessageEdit(event: { messageId: string, content: string, attachments?: any[] }) {
    const currentMessages = this.threadMessagesService.messages();
    const messageIndex = currentMessages.findIndex(m => m.id === event.messageId);

    if (messageIndex !== -1) {
      const originalMessageId = event.messageId;
      const updatedMessages = [...currentMessages];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        content: event.content,
        status: 'SENT'
      };

      const messagesBeforeAndIncludingEdit = updatedMessages.slice(0, messageIndex + 1);

      this.threadMessagesService.setMessages(messagesBeforeAndIncludingEdit);
      this.toolCallPanelService.initializeToolCallsFromMessages(messagesBeforeAndIncludingEdit);
      this.initializeTodos(messagesBeforeAndIncludingEdit);

      this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.PENDING);
      this.updateSidebarAgentStatus(THREAD_METADATA_STATUS.PENDING);

      const tools = this.threadInputComponent?.getSelectedToolsOptions().map(option => option.selectionTree).filter(tree => tree !== undefined) || [];
      const connectors = this.threadInputComponent?.getSelectedConnectors() || [];

      const payload: ChatMessagePayload = {
        query: event.content,
        provider: this.selectedProvider(),
        attachments: event.attachments || [],
        messageId: originalMessageId,
        checkpointMode: CHECKPOINT_MODE.EDIT,
        tools,
        connectors,
        mode: this.threadInputComponent?.selectedMode() ?? AGENT_MODE.ASK
      };

      this.agentService.runThread({
        ...payload,
        threadId: this.threadId(),
        threadMetadataId: this.threadMetadataId(),
        ...(this.documentId() ? { documentId: this.documentId() } : {})
      }).then(_res => {
        // handle success
      }).catch(err => {
        console.error('Failed to edit message', err);
        this.threadMessagesService.setThreadMetadataStatus(THREAD_METADATA_STATUS.ERROR);
      });
    }
  }

  submitFeedback(event: { messageId: string, feedback: MESSAGE_FEEDBACK }) {
    this.agentService.submitFeedback?.({
      threadId: this.threadId(),
      messageId: event.messageId,
      feedback: event.feedback,
      details: '',
      threadMetadataId: this.threadMetadataId() ?? ''
    }).subscribe(res => {
      if (res.success && res.data) {
        this.threadMessagesService.addMessageFeedback(res.data);
        this.toastService.success('Feedback submitted successfully');
      } else {
        this.toastService.error('Failed to submit feedback');
      }
    });
  }

  // ============================================
  // Workspace Mismatch
  // ============================================
  private getWorkspaces(): void {
    this.organizationService.getWorkspaces().subscribe({
      next: (res) => {
        if (res.success) {
          this.availableWorkspaces = res.data;
          // If the thread loaded before workspaces resolved, re-check now
          if (this.thread() && !this.workspaceMismatched()) {
            this.checkIfSameWorkspace();
          }
        } else {
          this.availableWorkspaces = [];
        }
      },
      error: () => {
        this.availableWorkspaces = [];
      }
    });
  }

  private checkIfSameWorkspace(): void {
    const t = this.thread();
    if (!t) return;

    const threadWorkspaceId = typeof t.workspace === 'string' ? t.workspace : (t.workspace as IWorkspace)?._id;
    const currentWorkspaceId = this.currentWorkspace?._id;

    const hasAccessToThreadWorkspace = this.availableWorkspaces.some(ws => ws._id === threadWorkspaceId);

    if (
      threadWorkspaceId &&
      threadWorkspaceId !== currentWorkspaceId &&
      hasAccessToThreadWorkspace &&
      !this.permissions().restricted
    ) {
      this.threadWorkspaceId = threadWorkspaceId;
      this.workspaceMismatched.set(true);
    }
  }

  // ============================================
  // Save-as-prompt dialog handlers
  // ============================================

  /**
   * Open the save-as-prompt dialog, optionally pre-filling the content field
   * with the message text that triggered the action (AI-message path).
   */
  openSaveAsPromptDialog(content: string = ''): void {
    this._enhancementOriginMessageId = null;
    this.saveAsPromptForm.set({
      title: '',
      content,
      groupIds: [],
      accessLevel: 'PERSONAL',
    });
    this._loadSaveAsPromptGroups();
    this.showSaveAsPromptDialog.set(true);
  }

  /**
   * Open the save-as-prompt dialog pre-filled from the Enhanced Prompt accordion
   * on a human message.  Stores the originating message ID so the saved prompt's
   * _id can be written back onto the human message after creation.
   */
  openSaveEnhancementDialog({ messageId, markdown }: { messageId: string; markdown: string }): void {
    this._enhancementOriginMessageId = messageId;
    this.saveAsPromptForm.set({
      title: '',
      content: markdown,
      groupIds: [],
      accessLevel: 'PERSONAL',
    });
    this._loadSaveAsPromptGroups();
    this.showSaveAsPromptDialog.set(true);
  }

  /**
   * Fetches the user's groups from the API and stores them as label/value pairs
   * for the Group multi-select in the save-as-prompt dialog.
   * Called each time the dialog opens so the list is always fresh.
   */
  private _loadSaveAsPromptGroups(): void {
    this.promptLibraryService.listGroups().then(res => {
      if (res?.success) {
        this.saveAsPromptGroupOptions.set(
          (res.data ?? []).map(g => ({ label: g.name, value: g._id }))
        );
      }
    }).catch(() => { /* non-critical — dropdown just won't show groups */ });
  }

  /** Update a single field in the save-as-prompt form without replacing the whole object. */
  updateSaveAsPromptField(field: keyof ICreatePromptPayload, value: any): void {
    this.saveAsPromptForm.update(current => ({ ...current, [field]: value }));
  }

  /** Close the save-as-prompt dialog and reset form state. */
  closeSaveAsPromptDialog(): void {
    this.showSaveAsPromptDialog.set(false);
    this._enhancementOriginMessageId = null;
    this.saveAsPromptForm.set({
      title: '',
      content: '',
      groupIds: [],
      accessLevel: 'PERSONAL',
    });
  }

  /** Submit the save-as-prompt form to the prompt library. */
  async submitSaveAsPrompt(): Promise<void> {
    const form = this.saveAsPromptForm();
    if (!form.title?.trim() || !form.content?.trim()) {
      this.toastService.error('Title and content are required');
      return;
    }
    this.isSavingPrompt.set(true);
    const originMessageId = this._enhancementOriginMessageId;
    try {
      const payload: ICreatePromptPayload = { ...form };
      if (originMessageId) {
        payload.threadMetadataId = this.threadMetadataId();
        payload.messageId = originMessageId;
      }
      const result = await this.promptLibraryService.createPrompt(payload);
      this.toastService.success('Prompt saved to library');

      // Tag the originating human message so the Save button disappears.
      if (originMessageId && result?.data?._id) {
        const promptId = result.data._id;
        const msg = this.threadMessagesService.getMessageById(originMessageId);
        if (msg) {
          const prevEnh = (msg.additional_kwargs ?? {})['approved_enhancement'] ?? {};
          this.threadMessagesService.updateMessage(originMessageId, {
            additional_kwargs: {
              ...(msg.additional_kwargs ?? {}),
              approved_enhancement: { ...prevEnh, prompt_id: promptId },
            },
          });
        }
      }

      this.closeSaveAsPromptDialog();
    } catch {
      this.toastService.error('Failed to save prompt');
    } finally {
      this.isSavingPrompt.set(false);
    }
  }

  /**
   * Called by PromptEditorDialogComponent's `(saved)` output.
   * Updates the backing form with the submitted values, then delegates to
   * `submitSaveAsPrompt()` which persists to the library as a PERSONAL prompt.
   */
  async onSaveAsPromptEditorSaved(form: { title: string; content: string; accessLevel: string; groupIds: string[] }): Promise<void> {
    this.saveAsPromptForm.update(f => ({
      ...f,
      title: form.title,
      content: form.content,
      accessLevel: form.accessLevel as ICreatePromptPayload['accessLevel'],
      groupIds: form.groupIds,
    }));
    await this.submitSaveAsPrompt();
  }

  switchToThreadWorkspace(): void {
    const currentUser = this.authService.getUser();
    if (!this.threadWorkspaceId || !currentUser?._id) {
      this.toastService.error('Unable to switch workspace');
      return;
    }

    this.userService.updateUser({
      _id: currentUser._id,
      currentWorkspace: this.threadWorkspaceId
    }).subscribe((res) => {
      if (res.success) {
        this.authService.setUserData(res.data);
        this.blurScreenService.enableBlur();
        this.workspaceMismatched.set(false);
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        this.toastService.error('Failed to switch workspace');
      }
    });
  }
}
